<template>
<div class="address-fields-component mb-2">
  <div class="address-fields-component__basic-info">
    <ValidationProvider rules="address_required|address_selected_from_list"
                        :name="streetInputPlaceholder"
                        :custom-messages="streetInputValidationMessages"
                        slim
    >
      <template slot-scope="{ errors }">
        <dadata-address-suggestions-input :label="streetInputLabel"
                                          :params="streetInputDaDataParams"
                                          :placeholder="streetInputPlaceholder"
                                          :restricted-to-city="addressSuggestionsAreRestrictedToCity"
                                          :error="errors[0]"
                                          ref="street"
                                          class="address-fields-component__street"
                                          @focus="scrollElToTop($refs.street.$el.querySelector('input'))"
                                          v-model="query"
        />
      </template>
    </ValidationProvider>
    <ValidationProvider rules="required"
                        name="Дом"
                        :custom-messages="{ required: 'Необходимо ввести дом' }"
                        slim
    >
      <template slot-scope="{ errors }">
        <CustomInput label="Дом *"
                     v-model="form.house"
                     :error="errors[0]"
                     class="address-fields-component__house"
                     type="text"
                     placeholder="Номер"
                     maxlength="10"
        />
      </template>
    </ValidationProvider>
    <CustomInput class="address-fields-component__house"
                 label="Корпус"
                 v-model="form.block"
                 type="text"
                 placeholder="Корпус"
    />
    <div v-if="collapsible" class="order-delivery__expand-button-wrapper">
      <label class="form-control-label invisible">.</label>
      <button @click="toggleDetailsCollapse"
              :aria-expanded="`${detailsCollapsed ? 'false' : 'true'}`"
              class="order-delivery__expand-button"
              type="button"
      >
        <IconArrowDown />
      </button>
    </div>
  </div>

  <TransitionExpand v-if="collapsible">
    <div v-show="!detailsCollapsed" class="address-fields-component__detail-info">
      <CustomInput class="address-fields-component__apt"
                   label="Квартира"
                   v-model="form.apartment"
                   inputmode="numeric"
                   placeholder="Номер"
      />
      <CustomInput class="address-fields-component__entrance"
                   label="Подъезд"
                   v-model="form.entrance"
                   inputmode="numeric"
                   placeholder="Номер"
      />
      <CustomInput class="address-fields-component__floor"
                   label="Этаж"
                   v-model="form.floor"
                   type="number"
                   inputmode="numeric"
                   placeholder="Номер"
      />
      <CustomSelect label="Домофон"
                    :options="hasIntercomOptions"
                    v-model="form.has_intercom"
                    :reduce="o => o.value"
                    class="address-fields-component__intercom"
                    :getOptionKey="o => o.value"
                    :clearable="false"
                    :searchable="false"
      />
    </div>
  </TransitionExpand>
  <div v-else class="address-fields-component__detail-info">
    <CustomInput class="address-fields-component__apt"
                 label="Квартира"
                 v-model="form.apartment"
                 inputmode="numeric"
                 placeholder="Номер"
    />
    <CustomInput class="address-fields-component__entrance"
                 label="Подъезд"
                 v-model="form.entrance"
                 inputmode="numeric"
                 placeholder="Номер"
    />
    <CustomInput class="address-fields-component__floor"
                 label="Этаж"
                 v-model="form.floor"
                 type="number"
                 inputmode="numeric"
                 placeholder="Номер"
    />
    <CustomSelect label="Домофон"
                  :options="hasIntercomOptions"
                  v-model="form.has_intercom"
                  :reduce="o => o.value"
                  class="address-fields-component__intercom"
                  :getOptionKey="o => o.value"
                  :clearable="false"
                  :searchable="false"
    />
  </div>
  <address-hint v-if="!editing" class="mt-2" />
</div>
</template>

<script>
import { ValidationProvider, extend } from '@/helpers/veeValidateCustomized'
import DadataAddressSuggestionsInput from '@/components/addresses/dadata/DadataAddressSuggestionsInput'
import CustomField from '@/components/inputs/CustomField'
import CustomInput from '@/components/inputs/CustomInput'
import CustomSelect from '@/components/inputs/CustomSelect'
import TransitionExpand from '@/components/AsyncTransitionExpand'
import { mapGetters } from 'vuex'
import getDaDataEntityName from '@/helpers/getDaDataEntityName'
import AddressHint from '@/components/addresses/AddressHint'

extend('address_required', {
  validate: v => v.text !== '',
  message: 'Обязательно',
});

extend('address_selected_from_list', {
  validate: v => !!v.suggestion,
  message: 'Необходимо выбрать из списка',
});

export default {
  props: {
    collapsible: {
      required: false,
      type: Boolean,
      default: true,
    },

    address: {
      required: false,
      type: Object,
      default: () => null,
    },

    scrollable: {
      required: false,
      type: Boolean,
      default: true,
    },

    editing: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  components: {
    DadataAddressSuggestionsInput,
    ValidationProvider,
    TransitionExpand,
    CustomField,
    CustomInput,
    CustomSelect,
    IconArrowDown: () => import('@/assets/images/icons/icon-arrow-down-small.svg?inline'),
    AddressHint,
  },

  created() {
    this.assignFormValues()
    this.detailsCollapsed = this.deliverySettings.extra_address_fields_state === 'collapsed'
  },

  computed: {
    ...mapGetters({
      activeCity: 'store/activeCity',
      deliverySettings: 'settings/delivery',
    }),

    addressSuggestionsAreRestrictedToCity() {
      if (!this.activeCity || !this.activeCity.settings) {
        return false
      } else if (!this.activeCity.settings.addresses) {
        return false
      }

      return this.activeCity.settings.addresses.restrict_suggestions === true
          && this.activeCity.settings.addresses.search_radius === null;
    },

    streetInputLabel() {
      if (this.addressSuggestionsAreRestrictedToCity) {
        return 'Улица *'
      }

      return 'Город и улица *'
    },

    streetInputPlaceholder() {
      if (this.addressSuggestionsAreRestrictedToCity) {
        return 'Улица'
      }

      return 'Город и улица'
    },

    streetInputValidationMessages() {
      if (this.addressSuggestionsAreRestrictedToCity) {
        return {
          address_selected_from_list: 'Улицу необходимо выбрать из списка',
          address_required: 'Необходимо ввести улицу'
        }
      }

      return {
        address_selected_from_list: 'Улицу необходимо выбрать из списка',
        address_required: 'Необходимо ввести город и улицу'
      }
    },

    streetInputDaDataParams() {
      if (!this.addressSuggestionsAreRestrictedToCity) {
        return {}
      }

      return {
        locations: [{ city: this.activeCity?.name }]
      }
    },

    hasIntercomOptions() {
      return [
        { value: true, label: 'Работает' },
        { value: false, label: 'Не работает' },
      ];
    },
  },

  methods: {
    assignFormValues() {
      if (!this.address) {
        return
      }

      this.query.text = this.getQueryTextForExistedAddress()

      this.form.address = this.address.full || ''
      this.form.house = this.address.house || ''
      this.form.block = this.address.block || ''
      this.form.apartment = this.address.apartment || ''
      this.form.entrance = this.address.entrance || ''
      this.form.floor = this.address.floor || ''
      this.form.has_intercom = this.address.has_intercom || false
      this.form.intercom_code = this.address.intercom_code || ''
      this.form.notes = this.address.notes || ''
    },

    scrollElToTop(el) {
      if (!this.scrollable) {
        return
      }

      // setTimeout so it doesn't get interrupted after a brief moment in Chrome for Android.
      // Also see the comment below.
      const initialTop = el.getBoundingClientRect().top

      setTimeout(() => {
        if (initialTop !== el.getBoundingClientRect().top) {
          // If scrolling is being handled for us.
          // To avoid the bug in Safari on iOS, where the modal is gets scrolled far past the input.
          return;
        }

        el.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }, 150);
    },

    suggestionPicked(suggestion) {
      this.inputEventsBlocked = true

      const address = this.getAddressValue(suggestion)
      this.query.text = this.addressSuggestionsAreRestrictedToCity ? address.short : address.full

      this.form.suggestion = suggestion

      if (suggestion.house) {
        this.form.house = suggestion.house
      }

      if (suggestion.block) {
        this.form.block = `${suggestion.block_type ? suggestion.block_type + ' ' : ''}${suggestion.block}`
      }

      if (suggestion.flat) {
        this.form.apartment = suggestion.flat
      }

      this.inputEventsBlocked = false

      this.$emit('input', this.getDeliveryAddress())
    },

    getQueryTextForExistedAddress() {
      if (!this.address) {
        return
      }

      let query = []

      if (this.address.city && !this.addressSuggestionsAreRestrictedToCity) {
        query.push(this.address.city)
      }

      if (this.address.settlement && query.indexOf(this.address.settlement) === -1) {
        query.push(this.address.settlement)
      }

      if (this.address.street && query.indexOf(this.address.street) === -1) {
        query.push(this.address.street)
      }

      return query.join(', ')
    },

    getAddressValue(suggestion) {
      const entityName = getDaDataEntityName(suggestion, ['street', 'settlement', 'city_district']);

      if (entityName === null) {
        throw new Error('Selected address does not have a single field that we qualify as "street"');
      }

      const entityWithType = suggestion[`${entityName}_with_type`]
      const cityEntityName = getDaDataEntityName(suggestion, ['city', 'settlement'])
      const cityEntityWithType = suggestion[`${cityEntityName}_with_type`]

      return {
        short: entityWithType,
        full: cityEntityWithType === entityWithType ? cityEntityWithType : `${cityEntityWithType}, ${entityWithType}`,
      };
    },

    toggleDetailsCollapse() {
      this.detailsCollapsed = !this.detailsCollapsed
    },

    getFullAddress() {
      if (!this.form.suggestion) {
        return null
      }

      const city = this.getCityPart(this.form.suggestion)
      const street = this.form.suggestion[`${getDaDataEntityName(this.form.suggestion, ['street', 'settlement', 'city_district'])}_with_type`]

      if (!city || !street) {
        return null
      }

      let address = ''

      if (city !== street) {
        address = `${city}, ${street}, ${this.getHousePart()}`
      } else {
        address = `${city}, ${this.getHousePart()}`
      }

      if (this.form.block) {
        address += ` ${this.form.block}`
      }

      return address
    },

    getCityPart(suggestion) {
      if (suggestion['settlement_with_type']) {
        return this.getSettlementPart(suggestion)
      } else if (suggestion['city_with_type']) {
        return suggestion['city_with_type']
      }

      return null
    },

    getSettlementPart(suggestion) {
      const city = suggestion['city_with_type']
      const area = suggestion['area_with_type']
      const region = suggestion['region_with_type']

      if (city) {
        return city === suggestion['settlement_with_type'] ? suggestion['settlement_with_type'] : `${city}, ${suggestion['settlement_with_type']}`
      } else if (region && area) {
        return `${region}, ${area}, ${suggestion['settlement_with_type']}`
      } else if (region) {
        return `${region}, ${suggestion['settlement_with_type']}`
      } else if (area) {
        return `${area}, ${suggestion['settlement_with_type']}`
      }

      return suggestion['settlement_with_type']
    },

    getHousePart() {
      if (isNaN(parseInt(this.form.house, 10))) {
        return this.form.house
      }

      return `д ${this.form.house}`
    },

    getDeliveryAddress() {
      return {
        address: this.getFullAddress(),
        apartment: this.form.apartment,
        entrance: this.form.entrance,
        floor: this.form.floor,
        has_intercom: this.form.has_intercom,
        intercom_code: this.form.intercom_code,
        notes: this.form.notes,
      }
    },
  },

  watch: {
    form: {
      deep: true,
      handler() {
        if (this.inputEventsBlocked) {
          return
        }

        this.$emit('input', this.getDeliveryAddress())
      },
    },

    ['query.suggestion'](val) {
      this.suggestionPicked(val)
    },
  },

  data() {
    return {
      suggestion: null,
      inputEventsBlocked: false,
      detailsCollapsed: true,
      query: {
        text: '',
        suggestion: null,
      },
      form: {
        suggestion: null,
        address: '',
        house: '',
        block: '',
        apartment: '',
        entrance: '',
        floor: '',
        has_intercom: false,
        intercom_code: '',
        notes: '',
      }
    }
  }
}
</script>
